import React, { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { Factory, Edit, Trash2, Plus, Target, icons, type LucideIcon } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { motion, type Variants } from 'framer-motion'
import { useToast } from '@/hooks/use-toast'
import { useIndustries, useIndustry, useCreateIndustry, useUpdateIndustry, useDeleteIndustry, useSetBenchmarks } from '@/features/industries/hooks'
import { useEngineDomains } from '@/features/domains/hooks'
import { IconPicker } from '@/components/ui/icon-picker'
import type { Industry } from '@/features/industries/types'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'

/** Resolve a lucide icon name string to a component, or fallback to Factory */
function resolveIcon(name?: string | null): LucideIcon {
    if (!name) return Factory
    return (icons as Record<string, LucideIcon>)[name] ?? Factory
}

const industryItemVariants: Variants = {
    hidden: { opacity: 0, y: 12 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.06, duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
    }),
}

interface BenchmarkRow {
    domain_id: string
    domain_name: string
    target_level: number
    description: string
}

export function SettingsIndustriesPage() {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const [editingIndustry, setEditingIndustry] = useState<Industry | null>(null)
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [industryToDelete, setIndustryToDelete] = useState<Industry | null>(null)
    const [managingIndustryId, setManagingIndustryId] = useState<string | null>(null)
    const [isBenchmarkDialogOpen, setIsBenchmarkDialogOpen] = useState(false)
    const [benchmarkRows, setBenchmarkRows] = useState<BenchmarkRow[]>([])
    const [createIcon, setCreateIcon] = useState('')
    const [editIcon, setEditIcon] = useState('')
    const { toast } = useToast()

    const { data: industries, isLoading, error } = useIndustries()
    const { data: managingIndustry } = useIndustry(managingIndustryId || '')
    const { data: domainsData } = useEngineDomains()
    const createMutation = useCreateIndustry()
    const updateMutation = useUpdateIndustry()
    const deleteMutation = useDeleteIndustry()
    const setBenchmarksMutation = useSetBenchmarks()

    // Extract domains list from API response
    const domains: Array<{ id: string; name: string }> = (() => {
        if (!domainsData) return []
        if (Array.isArray(domainsData)) return domainsData
        const d = (domainsData as any)?.data
        if (Array.isArray(d)) return d
        return []
    })()

    // When managing industry data is loaded, initialize benchmark rows
    useEffect(() => {
        if (managingIndustry && domains.length > 0 && isBenchmarkDialogOpen) {
            const existingBenchmarks = managingIndustry.benchmarks || []

            const bRows = domains.map(d => {
                const existing = existingBenchmarks.find(b => b.domain_id === d.id)
                return {
                    domain_id: d.id,
                    domain_name: d.name,
                    target_level: existing?.target_level ?? 3,
                    description: existing?.description ?? '',
                }
            })
            setBenchmarkRows(bRows)
        }
    }, [managingIndustry, domains, domains.length, isBenchmarkDialogOpen])

    const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)
        createMutation.mutate(
            {
                name: formData.get('name') as string,
                code: formData.get('code') as string,
                description: formData.get('description') as string,
                icon: createIcon || undefined,
            },
            {
                onSuccess: () => {
                    setIsCreateDialogOpen(false)
                    setCreateIcon('')
                    toast({ title: 'Industry created', description: 'New industry has been added.' })
                },
                onError: (error: any) => {
                    toast({
                        title: 'Error',
                        description: error.response?.data?.error || 'Failed to create industry',
                        variant: 'destructive',
                    })
                },
            }
        )
    }

    const handleUpdate = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!editingIndustry) return
        const formData = new FormData(e.currentTarget)
        updateMutation.mutate(
            {
                id: editingIndustry.id,
                data: {
                    name: formData.get('name') as string,
                    code: formData.get('code') as string,
                    description: formData.get('description') as string,
                    icon: editIcon || undefined,
                },
            },
            {
                onSuccess: () => {
                    setIsEditDialogOpen(false)
                    setEditingIndustry(null)
                    toast({ title: 'Industry updated', description: 'Industry has been updated.' })
                },
                onError: (error: any) => {
                    toast({
                        title: 'Error',
                        description: error.response?.data?.error || 'Failed to update industry',
                        variant: 'destructive',
                    })
                },
            }
        )
    }

    const handleDeleteConfirm = () => {
        if (!industryToDelete) return
        deleteMutation.mutate(industryToDelete.id, {
            onSuccess: () => {
                setIsDeleteDialogOpen(false)
                setIndustryToDelete(null)
                toast({ title: 'Industry deleted', description: 'Industry has been removed.' })
            },
            onError: (error: any) => {
                toast({
                    title: 'Error',
                    description: error.response?.data?.error || 'Failed to delete industry',
                    variant: 'destructive',
                })
            },
        })
    }

    const openBenchmarkDialog = (industryId: string) => {
        setManagingIndustryId(industryId)
        setIsBenchmarkDialogOpen(true)
    }

    const handleSaveBenchmarks = () => {
        if (!managingIndustryId) return
        setBenchmarksMutation.mutate(
            {
                id: managingIndustryId,
                benchmarks: benchmarkRows.map(r => ({
                    domain_id: r.domain_id,
                    target_level: r.target_level,
                    description: r.description || undefined,
                })),
            },
            {
                onSuccess: () => {
                    toast({ title: 'Benchmarks saved', description: 'Industry benchmark targets updated.' })
                    setIsBenchmarkDialogOpen(false)
                    setManagingIndustryId(null)
                },
                onError: (error: any) => {
                    toast({
                        title: 'Error',
                        description: error.response?.data?.error || 'Failed to save benchmarks',
                        variant: 'destructive',
                    })
                },
            }
        )
    }

    const listState = useListState({ defaultPerPage: 50 })

    // Client-side search filtering
    const filteredIndustries = useMemo(() => {
        if (!Array.isArray(industries)) return []
        const term = listState.search.toLowerCase()
        if (!term) return industries
        return industries.filter((ind: Industry) =>
            ind.name.toLowerCase().includes(term) ||
            ind.code?.toLowerCase().includes(term) ||
            ind.description?.toLowerCase().includes(term)
        )
    }, [industries, listState.search])

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Industries</h2>
                    <p className="text-gray-600">Failed to load industries. Please try again.</p>
                </div>
            </div>
        )
    }

    return (
        <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const }}
        >
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Factory className="h-5 w-5" />
                            All Industries
                        </CardTitle>
                        <CardDescription>
                            Industry verticals with benchmarks
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                        <ImportExportButtons
                            entityType="industries"
                            entityLabel="Industries"
                        />
                        <AuthorizationGate actions={['manage_industries']}>
                            <Button onClick={() => setIsCreateDialogOpen(true)}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add Industry
                            </Button>
                        </AuthorizationGate>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Search Toolbar */}
                    <DataToolbar
                        listState={listState}
                        searchPlaceholder="Search industries..."
                        totalItems={filteredIndustries.length}
                        showPerPage={false}
                    />

                    <div className="space-y-4 mt-4">
                        {filteredIndustries.length > 0 ? filteredIndustries.map((industry: Industry, index: number) => (
                            <motion.div
                                key={industry.id}
                                custom={index}
                                variants={industryItemVariants}
                                initial="hidden"
                                animate="visible"
                                className="flex items-center justify-between p-4 border rounded-lg"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center">
                                        {(() => { const Icon = resolveIcon(industry.icon); return <Icon className="h-5 w-5 text-white" /> })()}
                                    </div>
                                    <div>
                                        <div className="font-medium">{industry.name}</div>
                                        <div className="text-sm text-gray-500">{industry.description}</div>
                                        <div className="flex items-center gap-3 mt-1">
                                            <Badge variant="secondary" className="text-xs">{industry.code}</Badge>
                                            <Badge variant={industry.is_active ? 'default' : 'destructive'} className="text-xs">
                                                {industry.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <AuthorizationGate actions={['manage_industries']}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => openBenchmarkDialog(industry.id)}
                                            title="Manage Benchmarks"
                                        >
                                            <Target className="h-4 w-4 mr-1" />
                                            Benchmarks
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => { setEditingIndustry(industry); setEditIcon(industry.icon || ''); setIsEditDialogOpen(true) }}
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => { setIndustryToDelete(industry); setIsDeleteDialogOpen(true) }}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </AuthorizationGate>
                                </div>
                            </motion.div>
                        )) : (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No industries found. Add one to get started.</p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Create Industry Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Industry</DialogTitle>
                        <DialogDescription>Create a new industry vertical</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreate}>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="create-name">Name</Label>
                                <Input id="create-name" name="name" placeholder="e.g. Healthcare & Life Sciences" required />
                            </div>
                            <div>
                                <Label htmlFor="create-code">Code</Label>
                                <Input id="create-code" name="code" placeholder="e.g. healthcare" required />
                            </div>
                            <div>
                                <Label htmlFor="create-description">Description</Label>
                                <Textarea id="create-description" name="description" placeholder="Brief description" rows={3} />
                            </div>
                            <IconPicker value={createIcon} onChange={setCreateIcon} />
                        </div>
                        <DialogFooter className="mt-4">
                            <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={createMutation.isPending}>
                                {createMutation.isPending ? 'Creating...' : 'Create Industry'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Industry Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Industry</DialogTitle>
                        <DialogDescription>Update industry information</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleUpdate} key={editingIndustry?.id}>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="edit-name">Name</Label>
                                <Input id="edit-name" name="name" defaultValue={editingIndustry?.name} required />
                            </div>
                            <div>
                                <Label htmlFor="edit-code">Code</Label>
                                <Input id="edit-code" name="code" defaultValue={editingIndustry?.code} required />
                            </div>
                            <div>
                                <Label htmlFor="edit-description">Description</Label>
                                <Textarea id="edit-description" name="description" defaultValue={editingIndustry?.description ?? ''} rows={3} />
                            </div>
                            <IconPicker value={editIcon} onChange={setEditIcon} />
                        </div>
                        <DialogFooter className="mt-4">
                            <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={updateMutation.isPending}>
                                {updateMutation.isPending ? 'Updating...' : 'Update Industry'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Industry Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Industry</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete "{industryToDelete?.name}"? Organizations using this industry will be unlinked. This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteConfirm}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending ? 'Deleting...' : 'Delete Industry'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Benchmarks Dialog */}
            <Dialog open={isBenchmarkDialogOpen} onOpenChange={(open) => {
                setIsBenchmarkDialogOpen(open)
                if (!open) setManagingIndustryId(null)
            }}>
                <DialogContent className="max-w-2xl flex flex-col max-h-[85vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Target className="h-5 w-5" />
                            {managingIndustry?.name} — Benchmarks
                        </DialogTitle>
                        <DialogDescription>
                            Set per-domain maturity targets for this industry
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto space-y-4 mt-4 pr-1">
                        <p className="text-sm text-gray-500">
                            Set the target maturity level (1–5) for each domain in this industry.
                        </p>
                        {benchmarkRows.map((row, idx) => (
                            <div key={row.domain_id} className="p-3 border rounded-lg space-y-2">
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <Label className="text-sm font-medium">{row.domain_name}</Label>
                                    </div>
                                    <div className="w-56">
                                        <select
                                            value={row.target_level}
                                            onChange={(e) => {
                                                const updated = [...benchmarkRows]
                                                updated[idx] = { ...updated[idx], target_level: parseInt(e.target.value) }
                                                setBenchmarkRows(updated)
                                            }}
                                            className="w-full h-9 px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value={1}>Level 1 — Initial</option>
                                            <option value={2}>Level 2 — Managed</option>
                                            <option value={3}>Level 3 — Defined</option>
                                            <option value={4}>Level 4 — Quantitatively Managed</option>
                                            <option value={5}>Level 5 — Optimizing</option>
                                        </select>
                                    </div>
                                </div>
                                <Textarea
                                    placeholder="Description (optional)"
                                    value={row.description}
                                    rows={2}
                                    onChange={(e) => {
                                        const updated = [...benchmarkRows]
                                        updated[idx] = { ...updated[idx], description: e.target.value }
                                        setBenchmarkRows(updated)
                                    }}
                                    className="text-sm"
                                />
                            </div>
                        ))}
                        {benchmarkRows.length === 0 && (
                            <p className="text-center py-4 text-gray-500">
                                No domains found. Create domains first.
                            </p>
                        )}
                    </div>

                    <div className="flex justify-end pt-4 mt-2 border-t">
                        <Button
                            onClick={handleSaveBenchmarks}
                            disabled={setBenchmarksMutation.isPending}
                        >
                            {setBenchmarksMutation.isPending ? 'Saving...' : 'Save Benchmarks'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </motion.div>
    )
}
